<template>
  <layout-placeholder
    v-if="pageLayout && contentOnly"
    :blocks="pageLayout.props.blocks"
    :external-data="externalData"
    :breadcrumbs="pageLayout.breadcrumbs"
  />
  <component
    :is="pageLayout.componentName"
    v-if="pageLayout && !contentOnly"
    :blocks="pageLayout.props.blocks"
    :breadcrumbs="pageLayout.breadcrumbs"
    :external-data="externalData"
    :properties="pageLayout.properties"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import camelCase from 'lodash-es/camelCase';
import upperFirst from 'lodash-es/upperFirst';
import {
  ref,
  showError,
  useAsyncData,
  useHead,
  useRoute,
  useServerSeoMeta,
  onBeforeRouteLeave,
  useNuxtApp
} from '#imports';
import { cmsContentStore } from '#sitis/stores/modules/cms-content-store';
import { ContentCmsPage, type IContentBlockCms, type IContentCms } from '#sitis/internal/controllers/cms/models/Content';

import { appConfigStore } from '#sitis/stores/modules/app-store';
import type { Ref } from 'vue';
import { watch } from 'vue';
import type { IBreadcrumbs } from '#sitis/stores/modules/catalog-store';

interface IExtraMeta {
	name: string;
	content: string;
}

interface IPageLayout {
	componentName: string;
	breadcrumbs: IBreadcrumbs[];
	props: {
		breadcrumbs: Record<string, string>[];
		blocks: IContentBlockCms[];
		externalData: undefined;
	};
	properties?: any;
}

interface ICmsPageMeta {
	title: string;
	meta: any[];
	externalData?: any;
}

const props = withDefaults(defineProps<{
	pageName?: string;
	pageId?: number;
	level?: number;
	contentOnly?: boolean;
	hideHead?: boolean;
	externalData?: any;
}>(), {
  contentOnly: false,
  hideHead: false,
  level: 0
});
const { ssrContext, $api } = useNuxtApp();
const cmsStore = cmsContentStore();
const { configList } = appConfigStore();
const pageLayout = ref<IPageLayout | null>();

const {
  data: page,
  error,
  pending
} = await useAsyncData(`cms-content-page-${props.pageId || props.pageName}`, async () => {
  if (props.pageName !== 'homepage') {
    return await cmsStore.getPage({ pageIdOrName: props.pageId || props.pageName });
  }

  const resp = await $api.agent.get<IContentCms>('/cms/home').then((res) => res._data);
  if (resp) {
    return new ContentCmsPage(resp).toJSON();
  }
}, {
  watch: [() => props.pageId, () => props.pageName]
});

watch(() => error.value, () => checkPageError());
watch(() => pending.value, () => checkPageError());

onBeforeRouteLeave((to, from, next) => {
  pageLayout.value = null;
  return next();
});

const initialCmsPage = () => {
  if (!page?.value) {
    pageLayout.value = null;
    return;
  }
  if (!props.contentOnly) {
    seoHandler(page);
  }

  const pageLayoutInit = ref<IPageLayout | null>({
    componentName: '',
    props: {
      blocks: [],
      breadcrumbs: [],
      externalData: undefined
    },
    breadcrumbs: []
  });
  const $route = useRoute();

  const layoutName = upperFirst(camelCase(page.value?.layout?.name.replace(/[\s-_]*/, '')));

	pageLayoutInit.value!.componentName = `${layoutName}Layout`;
	pageLayoutInit.value!.properties = page.value?.properties;

	const blocks: IContentBlockCms[] = page?.value?.placeholders.content;
	pageLayoutInit.value!.props = {
	  blocks,
	  breadcrumbs: [],
	  externalData: props.externalData
	};

	const pathParts = $route.path.split('/').filter((p) => !!p);

	if (pathParts.length !== 0) {
		pageLayoutInit.value!.breadcrumbs.push({
		  title: 'Главная',
		  path: '/'
		});

		pageLayoutInit.value!.props.breadcrumbs.push({
		  title: 'Главная',
		  path: '/'
		});

		const { flatMenuList } = storeToRefs(cmsStore);

		for (const slug of pathParts) {
		  const menu = flatMenuList.value.find((m: any) => m.alias === slug);
		  if (!menu) {
		    continue;
		  }

			pageLayoutInit.value!.breadcrumbs.push({
			  path: `/${menu.path}`,
			  title: menu.title
			});
			pageLayoutInit.value!.props.breadcrumbs.push({
			  path: `/${menu.path}`,
			  title: menu.title
			});
		}
	}

	pageLayout.value = pageLayoutInit.value;
};
const checkPageError = () => {
  if (pending.value) {
    return;
  }
  if (!error.value) {
    return;
  }
  showError({ statusCode: 404, message: 'Page not found' });
};

const seoHandler = (page: Ref<IContentCms | undefined | null>): void => {
  const extraMeta = initialExtraMeta(configList?.head?.meta);

  useHead({
    title: () => `${page?.value?.page?.titleTag || page?.value?.page?.title || ''}`,
    titleTemplate: `%s ${configList?.suffixDivider || ''} ${
			configList?.suffixTitle || ''}`,
    meta: [
      { name: 'description', content: () => `${page?.value?.page.description || ''}` },
      { name: 'keywords', content: () => `${page?.value?.page.keywords || ''}` },
      ...extraMeta
    ]
  });

  useServerSeoMeta({
    ogTitle: () => `${page?.value?.page?.titleTag || page?.value?.page?.title || ''}`,
    ogDescription: () => `${page?.value?.page.description || ''}`,
    ogType: 'website',
    ogLocale: 'ru_RU',
    ogUrl: () => (process.server ? ssrContext!.event.node.req.headers.host : window.location.host),
    ogImage: () => `${configList?.logoFront || ''}`,
    ogSiteName: () => `${configList?.siteName || ''}`,

    twitterDescription: () => `${page?.value?.page.description || ''}`,
    twitterImage: () => `${configList?.logoFront || ''}`,
    twitterTitle: () => `${page?.value?.page?.titleTag || page?.value?.page?.title || ''}`,
    twitterCard: 'summary'
  });
};
const initialExtraMeta = (options: IExtraMeta[] = []) => options.map((t) => ({ name: t.name, content: t.content }));

initialCmsPage();
checkPageError();
</script>
